import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useEmployeeBillingRate } from "@/hooks/use-employee-billing-rate";
import { useEmployeeBf } from "@/hooks/use-employee-bf";
import { useCanAccessMenu } from "@/hooks/use-menu-permissions";
import { useMonthlyConfirmations } from "@/hooks/use-monthly-confirmations";
import { useRBAC } from "@/hooks/use-rbac";
import { BillingRateBreakdownDialog } from "@/components/employee/BillingRateBreakdownDialog";
import { BfMaalSection } from "@/components/employee/BfMaalSection";
import { formatNOK, formatPct, MONTH_NAMES_NB } from "@/lib/format";
import { billingStatus, TONE_TEXT } from "@/lib/widget-status";
import { KpiTile } from "./KpiTile";
import { MonthlyRateList } from "./MonthlyRateList";
import { TabLoading } from "./TabLoading";

interface MaalTabProps {
  userId: string;
  targetBillingRate: number | null;
}

export function MaalTab({ userId, targetBillingRate }: MaalTabProps) {
  const year = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const { data: employee, isLoading } = useEmployeeBillingRate(userId, year);
  const { data: bf } = useEmployeeBf(userId, year);
  const canSeeDetalj = useCanAccessMenu("faktureringsgrad-detalj");
  const { isAdmin } = useRBAC();
  const { data: confirmations = [] } = useMonthlyConfirmations(userId, year);
  const [openMonth, setOpenMonth] = useState<number | null>(null);

  if (isLoading) return <TabLoading />;

  const lockedMonths = new Set(confirmations.map((c) => c.month));
  const target = targetBillingRate ?? employee?.targetRate ?? null;
  const monthRate = employee?.months.find((m) => m.month === currentMonth)?.rate ?? null;
  const ytdRate = employee?.ytdRate ?? null;
  const openMonthData = employee?.months.find((m) => m.month === openMonth) ?? null;

  const rateTile = (rate: number | null) => ({
    value: rate != null ? formatPct(rate) : "—",
    valueClassName: rate != null && target != null ? TONE_TEXT[billingStatus(rate, target).tone] : undefined,
  });
  const targetText = target != null ? `Mål ${formatPct(target, 0)}` : "Mål ikke satt";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiTile
          label={`Faktureringsgrad ${MONTH_NAMES_NB[currentMonth - 1].toLowerCase()}`}
          {...rateTile(monthRate)}
          sub={`${targetText}${lockedMonths.has(currentMonth) ? "" : " · måneden er ikke lukket"}`}
        />
        <KpiTile label={`Hittil i ${year}`} {...rateTile(ytdRate)} sub={targetText} />
        <KpiTile
          label="GEEK-bidrag hittil i år"
          value={bf?.hasUnit ? formatNOK(bf.ytdBidragKr) : "—"}
          sub={bf?.hasUnit ? `${MONTH_NAMES_NB[currentMonth - 1]}: ${formatNOK(bf.monthBidragKr)}` : "Ikke medlem av en selgergruppe"}
        />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        {employee && employee.months.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-2 px-5 py-3">
                <h2 className="font-display text-base font-semibold">Faktureringsgrad per måned</h2>
                {target != null && (
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="inline-block h-3 w-0.5 bg-foreground" aria-hidden="true" />
                    mål {formatPct(target, 0)}
                  </span>
                )}
              </div>
              <div className="border-t border-border px-5 py-1">
                <MonthlyRateList
                  userId={userId}
                  year={year}
                  months={employee.months}
                  target={target}
                  lockedMonths={lockedMonths}
                  canUnlock={isAdmin}
                  onOpenMonth={canSeeDetalj ? setOpenMonth : undefined}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* VIB-632: BF-mål, enhetens delte mål og oppnåelse */}
        {bf && <BfMaalSection data={bf} year={year} />}
      </div>

      {canSeeDetalj && employee && (
        <BillingRateBreakdownDialog
          open={openMonth != null}
          onOpenChange={(o) => !o && setOpenMonth(null)}
          userId={userId}
          fullName={employee.fullName ?? ""}
          year={year}
          month={openMonth}
          capacity={openMonthData?.capacity ?? 0}
          rate={openMonthData?.rate ?? null}
        />
      )}
    </div>
  );
}
